/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  MutationKey,
  useMutation,
  UseMutationResult,
} from "@tanstack/react-query";
import { GramCore } from "../core.js";
import { accessSetAIToolDecision } from "../funcs/accessSetAIToolDecision.js";
import { combineSignals } from "../lib/primitives.js";
import { RequestOptions } from "../lib/sdks.js";
import { SetAIToolDecisionResult } from "../models/components/setaitooldecisionresult.js";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  SetAIToolDecisionRequest,
  SetAIToolDecisionSecurity,
} from "../models/operations/setaitooldecision.js";
import { unwrapAsync } from "../types/fp.js";
import { useGramContext } from "./_context.js";
import { MutationHookOptions } from "./_types.js";

export type SetAIToolDecisionMutationVariables = {
  request: SetAIToolDecisionRequest;
  security?: SetAIToolDecisionSecurity | undefined;
  options?: RequestOptions;
};

export type SetAIToolDecisionMutationData = SetAIToolDecisionResult;

export type SetAIToolDecisionMutationError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * setAIToolDecision access
 *
 * @remarks
 * Record whether a detected AI tool may reach this organization's MCP gateway. The decision is organization-level and applies to every server: a blocked tool is refused when it authenticates, so its users see an error their client cannot recover from. Enforcement needs a credential Speakeasy can verify, so a tool that carries no verifiable gateway matcher — one linked only by a self-reported client name, or by nothing at all — cannot be decided on: the request is rejected with bad_request, nothing is recorded, and no summary is returned. An id the organization's scan target catalog does not know is rejected with not_found. Requires an authenticated session authorized for org:admin on the active organization.
 */
export function useSetAIToolDecisionMutation(
  options?: MutationHookOptions<
    SetAIToolDecisionMutationData,
    SetAIToolDecisionMutationError,
    SetAIToolDecisionMutationVariables
  >,
): UseMutationResult<
  SetAIToolDecisionMutationData,
  SetAIToolDecisionMutationError,
  SetAIToolDecisionMutationVariables
> {
  const client = useGramContext();
  return useMutation({
    ...buildSetAIToolDecisionMutation(client, options),
    ...options,
  });
}

export function mutationKeySetAIToolDecision(): MutationKey {
  return ["@gram/client", "access", "setAIToolDecision"];
}

export function buildSetAIToolDecisionMutation(
  client$: GramCore,
  hookOptions?: RequestOptions,
): {
  mutationKey: MutationKey;
  mutationFn: (
    variables: SetAIToolDecisionMutationVariables,
  ) => Promise<SetAIToolDecisionMutationData>;
} {
  return {
    mutationKey: mutationKeySetAIToolDecision(),
    mutationFn: function setAIToolDecisionMutationFn({
      request,
      security,
      options,
    }): Promise<SetAIToolDecisionMutationData> {
      const mergedOptions = {
        ...hookOptions,
        ...options,
        fetchOptions: {
          ...hookOptions?.fetchOptions,
          ...options?.fetchOptions,
          signal: combineSignals(
            hookOptions?.fetchOptions?.signal,
            options?.fetchOptions?.signal,
          ),
        },
      };
      return unwrapAsync(accessSetAIToolDecision(
        client$,
        request,
        security,
        mergedOptions,
      ));
    },
  };
}
